import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
    Search,
    Fingerprint,
    QrCode,
    Plus,
    Minus,
    Trash2,
    ShoppingCart,
    Check,
    X,
    ShieldAlert,
    LogOut,
    Loader2,
    ArrowLeft,
    AlertTriangle,
} from "lucide-react";
import { BioButton } from "@/components/bio/BioButton";
import { Input } from "@/components/ui/input";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LanguageToggle } from "@/components/LanguageToggle";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { api } from "@/lib/api";
import { formatUSD } from "@/lib/format";

const BIOFOOD_LOGO = "/biofood-blue.png";

export default function POS() {
    const { user, logout } = useAuth();
    const { t } = useLang();
    const [catalog, setCatalog] = useState({ products: [], categories: [] });
    const [cat, setCat] = useState("all");
    const [q, setQ] = useState("");
    const [cart, setCart] = useState({});
    const [roster, setRoster] = useState([]);
    const [identifyOpen, setIdentifyOpen] = useState(false);
    const [scanning, setScanning] = useState(false);
    const [student, setStudent] = useState(null);
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        api.get("/pos/catalog").then(({ data }) => setCatalog(data));
        api.get("/pos/roster").then(({ data }) => setRoster(data));
    }, []);

    useEffect(() => {
        const params = {};
        if (cat !== "all") params.category = cat;
        if (q) params.q = q;
        api.get("/pos/catalog", { params }).then(({ data }) => setCatalog((c) => ({ ...c, products: data.products })));
    }, [cat, q]);

    const items = useMemo(
        () => Object.values(cart).filter((i) => i.qty > 0),
        [cart],
    );
    const total = useMemo(() => items.reduce((s, i) => s + i.price * i.qty, 0), [items]);

    const add = (p) => setCart((c) => ({ ...c, [p.id]: { ...p, qty: (c[p.id]?.qty || 0) + 1 } }));
    const dec = (p) => setCart((c) => {
        const qty = (c[p.id]?.qty || 0) - 1;
        const n = { ...c };
        if (qty <= 0) delete n[p.id];
        else n[p.id] = { ...c[p.id], qty };
        return n;
    });
    const removeItem = (id) => setCart((c) => { const n = { ...c }; delete n[id]; return n; });

    const startScan = (method) => {
        setScanning(method);
        setStudent(null);
    };

    const pickStudent = async (s) => {
        setScanning("loading");
        try {
            const { data } = await api.post("/pos/identify", { student_id: s.id });
            setStudent(data);
        } finally {
            setScanning(false);
        }
    };

    const charge = async () => {
        if (!items.length) return;
        if (!student) { setIdentifyOpen(true); return; }
        setLoading(true);
        try {
            const { data } = await api.post("/pos/checkout", {
                student_id: student.id,
                items: items.map((i) => ({ product_id: i.id, qty: i.qty })),
                method: "biometric",
            });
            setResult(data);
            if (data.ok) {
                toast.success(t("pos.approved"));
                setCart({});
            }
        } finally {
            setLoading(false);
        }
    };

    const newSale = () => {
        setResult(null);
        setStudent(null);
        setCart({});
    };

    const cats = ["all", ...catalog.categories];

    return (
        <div className="h-screen flex flex-col bg-bio-bg font-poppins text-bio-text overflow-hidden">
            {/* Topbar */}
            <header className="h-14 shrink-0 bg-white border-b border-bio-border-subtle flex items-center justify-between px-4">
                <div className="flex items-center gap-2.5">
                    <img src={BIOFOOD_LOGO} alt="BIOFOOD" className="h-7 w-auto" />
                    <span className="text-bio-text-muted font-semibold text-sm">POS</span>
                </div>
                <div className="flex items-center gap-3">
                    <LanguageToggle />
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <button data-testid="user-menu" className="h-8 w-8 rounded-full bg-bio-gradient text-white font-bold text-sm flex items-center justify-center">{(user?.name || "O").charAt(0)}</button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="rounded-bio-card">
                            <div className="px-3 py-2"><p className="text-sm font-semibold">{user?.name}</p><p className="text-xs text-bio-text-muted">{user?.role_label}</p></div>
                            <DropdownMenuItem data-testid="logout-btn" onClick={logout} className="text-bio-error cursor-pointer"><LogOut className="h-4 w-4 mr-2" /> {t("common.logout")}</DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </header>

            <div className="flex-1 flex min-h-0">
                {/* Left: catalog */}
                <div className="flex-1 flex flex-col min-w-0 p-4 gap-3">
                    <div className="flex gap-3 items-center">
                        <div className="relative flex-1">
                            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-bio-text-muted" />
                            <Input data-testid="pos-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("pos.search")} className="h-11 pl-10 rounded-bio bg-white border-[1.5px]" />
                        </div>
                    </div>
                    <div className="flex gap-2 overflow-x-auto pb-1">
                        {cats.map((c) => (
                            <button key={c} data-testid={`cat-${c}`} onClick={() => setCat(c)} className={`shrink-0 px-4 h-9 rounded-full text-sm font-semibold transition-colors ${cat === c ? "bg-bio-primary text-white" : "bg-white border border-bio-border-subtle text-bio-text-secondary hover:border-bio-accent"}`}>
                                {c === "all" ? t("pos.all") : c}
                            </button>
                        ))}
                    </div>
                    <div className="flex-1 overflow-y-auto">
                        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3">
                            {catalog.products.map((p) => (
                                <button key={p.id} data-testid={`product-${p.id}`} onClick={() => add(p)} className="text-left bg-white rounded-bio-card border border-bio-border-subtle overflow-hidden hover:border-bio-accent hover:shadow-bio-card transition-all active:scale-95">
                                    <div className="relative">
                                        <img src={p.image} alt={p.name} className="h-24 w-full object-cover" />
                                        {p.restrictions?.length > 0 && (
                                            <span className="absolute top-1.5 right-1.5 h-6 w-6 rounded-full bg-[#FEF3C7] text-[#B45309] flex items-center justify-center" title="Restricciones"><ShieldAlert className="h-3.5 w-3.5" /></span>
                                        )}
                                    </div>
                                    <div className="p-2.5">
                                        <p className="text-sm font-semibold leading-tight line-clamp-2">{p.name}</p>
                                        <p className="text-bio-primary font-bold mt-1">{formatUSD(p.price)}</p>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Right: cart */}
                <aside className="w-[340px] shrink-0 bg-white border-l border-bio-border-subtle flex flex-col">
                    <div className="p-4 border-b border-bio-border-subtle flex items-center gap-2">
                        <ShoppingCart className="h-5 w-5 text-bio-primary" />
                        <span className="font-bold">{t("pos.cart")}</span>
                        <span className="ml-auto text-sm text-bio-text-muted">{items.length} {t("pos.items")}</span>
                    </div>

                    {student && (
                        <div className="m-3 rounded-bio-card border border-bio-border-subtle p-3 flex items-center gap-3 bg-bio-bg">
                            <img src={student.photo} alt="" className="h-11 w-11 rounded-full bg-white" />
                            <div className="min-w-0 flex-1">
                                <p className="text-sm font-bold truncate">{student.name}</p>
                                <p className="text-xs text-bio-text-secondary">{t("pos.balance")}: <span className="font-semibold">{formatUSD(student.balance)}</span></p>
                            </div>
                            {student.restrictions?.length > 0 && <ShieldAlert className="h-4 w-4 text-[#B45309]" />}
                        </div>
                    )}

                    <div className="flex-1 overflow-y-auto px-3">
                        {items.length === 0 ? (
                            <div className="h-full flex flex-col items-center justify-center text-center text-bio-text-muted">
                                <ShoppingCart className="h-10 w-10 mb-2 opacity-40" />
                                <p className="text-sm">{t("pos.emptyCart")}</p>
                            </div>
                        ) : (
                            <div className="space-y-2 py-2">
                                {items.map((i) => (
                                    <div key={i.id} className="flex items-center gap-2" data-testid={`cart-${i.id}`}>
                                        <img src={i.image} alt="" className="h-10 w-10 rounded-bio object-cover" />
                                        <div className="min-w-0 flex-1">
                                            <p className="text-sm font-semibold truncate">{i.name}</p>
                                            <p className="text-xs text-bio-text-muted">{formatUSD(i.price)}</p>
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            <button onClick={() => dec(i)} className="h-7 w-7 rounded-bio border border-bio-border flex items-center justify-center hover:bg-bio-bg"><Minus className="h-3.5 w-3.5" /></button>
                                            <span className="w-5 text-center text-sm font-bold">{i.qty}</span>
                                            <button onClick={() => add(i)} className="h-7 w-7 rounded-bio border border-bio-border flex items-center justify-center hover:bg-bio-bg"><Plus className="h-3.5 w-3.5" /></button>
                                        </div>
                                        <button onClick={() => removeItem(i.id)} className="text-bio-text-muted hover:text-bio-error"><Trash2 className="h-4 w-4" /></button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="p-4 border-t border-bio-border-subtle space-y-3">
                        <div className="flex items-center justify-between">
                            <span className="text-bio-text-secondary">{t("pos.total")}</span>
                            <span className="text-2xl font-extrabold" data-testid="pos-total">{formatUSD(total)}</span>
                        </div>
                        <BioButton data-testid="pos-charge" className="w-full h-14 text-base" disabled={!items.length} loading={loading} onClick={charge}>
                            {student ? <><Check className="h-5 w-5" /> {t("pos.charge")}</> : <><Fingerprint className="h-5 w-5" /> {t("pos.identify")}</>}
                        </BioButton>
                    </div>
                </aside>
            </div>

            {/* Identify dialog */}
            <Dialog open={identifyOpen} onOpenChange={setIdentifyOpen}>
                <DialogContent className="rounded-bio-lg max-w-md border-bio-border-subtle">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-bold">{t("pos.identify")}</DialogTitle>
                        <DialogDescription>{t("pos.pickStudent")}</DialogDescription>
                    </DialogHeader>
                    {!scanning && !student && (
                        <div className="grid grid-cols-2 gap-3 py-2">
                            <button data-testid="scan-fingerprint" onClick={() => startScan("fingerprint")} className="rounded-bio-card border-[1.5px] border-bio-border p-5 flex flex-col items-center gap-2 hover:border-bio-accent hover:bg-[#EEF7FC] transition-colors">
                                <Fingerprint className="h-8 w-8 text-bio-primary" /><span className="text-sm font-semibold">{t("pos.scanFinger")}</span>
                            </button>
                            <button data-testid="scan-qr" onClick={() => startScan("qr")} className="rounded-bio-card border-[1.5px] border-bio-border p-5 flex flex-col items-center gap-2 hover:border-bio-accent hover:bg-[#EEF7FC] transition-colors">
                                <QrCode className="h-8 w-8 text-bio-primary" /><span className="text-sm font-semibold">{t("pos.scanQr")}</span>
                            </button>
                        </div>
                    )}
                    {scanning && scanning !== "loading" && (
                        <div className="py-2">
                            <div className="flex flex-col items-center py-4">
                                <div className="relative h-20 w-20 flex items-center justify-center">
                                    <motion.div className="absolute inset-0 rounded-full border-4 border-bio-accent/30" animate={{ scale: [1, 1.15, 1] }} transition={{ repeat: Infinity, duration: 1.4 }} />
                                    {scanning === "fingerprint" ? <Fingerprint className="h-10 w-10 text-bio-primary" /> : <QrCode className="h-10 w-10 text-bio-primary" />}
                                </div>
                                <p className="text-sm text-bio-text-secondary mt-3">{t("pos.pickStudent")}</p>
                            </div>
                            <div className="max-h-64 overflow-y-auto space-y-1.5">
                                {roster.map((s) => (
                                    <button key={s.id} data-testid={`roster-${s.id}`} onClick={() => pickStudent(s)} className="w-full flex items-center gap-3 rounded-bio border border-bio-border-subtle p-2.5 hover:border-bio-accent hover:bg-[#EEF7FC] transition-colors text-left">
                                        <img src={s.photo} alt="" className="h-9 w-9 rounded-full bg-bio-bg" />
                                        <div className="min-w-0"><p className="text-sm font-semibold truncate">{s.name}</p><p className="text-xs text-bio-text-muted">{s.grade} · {s.bio_id}</p></div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                    {scanning === "loading" && (
                        <div className="py-10 flex flex-col items-center"><Loader2 className="h-8 w-8 animate-spin text-bio-primary" /><p className="text-sm text-bio-text-muted mt-2">{t("pos.scanning")}</p></div>
                    )}
                    {student && !scanning && (
                        <div className="py-2">
                            <div className="flex items-center gap-3 rounded-bio-card bg-bio-bg p-4">
                                <img src={student.photo} alt="" className="h-14 w-14 rounded-full bg-white" />
                                <div className="flex-1 min-w-0">
                                    <p className="font-bold truncate">{student.name}</p>
                                    <p className="text-sm text-bio-text-secondary">{student.grade}</p>
                                    <p className="text-sm">{t("pos.balance")}: <span className="font-bold text-bio-primary">{formatUSD(student.balance)}</span></p>
                                </div>
                            </div>
                            {student.restrictions?.length > 0 && (
                                <div className="mt-3 flex items-center gap-2 text-sm text-[#B45309] bg-[#FEF3C7] rounded-bio px-3 py-2">
                                    <ShieldAlert className="h-4 w-4" /> {student.restrictions.join(", ")}
                                </div>
                            )}
                            <BioButton className="w-full mt-4" data-testid="identify-confirm" onClick={() => setIdentifyOpen(false)}>{t("common.continue")}</BioButton>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Result dialog */}
            <Dialog open={!!result} onOpenChange={(v) => { if (!v) { if (result?.ok) newSale(); setResult(null); } }}>
                <DialogContent className="rounded-bio-lg max-w-sm border-bio-border-subtle">
                    <DialogHeader><DialogTitle className="sr-only">{result?.ok ? t("pos.approved") : t("pos.declined")}</DialogTitle><DialogDescription className="sr-only">Resultado de la venta</DialogDescription></DialogHeader>
                    {result && (
                        <div className="text-center py-2" data-testid="pos-result">
                            <div className={`mx-auto h-16 w-16 rounded-full flex items-center justify-center mb-3 ${result.ok ? "bg-[#E8F9F8] text-[#0F807B]" : "bg-[#FDECEA] text-[#B71C1C]"}`}>
                                {result.ok ? <Check className="h-8 w-8" /> : <X className="h-8 w-8" />}
                            </div>
                            <h3 className="text-xl font-bold">{result.ok ? t("pos.approved") : t("pos.declined")}</h3>
                            {!result.ok && (
                                <div className="mt-2 flex items-center justify-center gap-2 text-sm text-bio-error bg-[#FDECEA] rounded-bio px-3 py-2 mx-auto w-fit">
                                    <AlertTriangle className="h-4 w-4" /> {result.message}
                                </div>
                            )}
                            <div className="mt-4 bg-bio-bg rounded-bio-card p-4 text-left text-sm space-y-1.5">
                                <div className="flex justify-between"><span className="text-bio-text-secondary">{t("pos.total")}</span><span className="font-bold">{formatUSD(result.total)}</span></div>
                                {result.ok && <div className="flex justify-between"><span className="text-bio-text-secondary">{t("pos.newBalance")}</span><span className="font-bold">{formatUSD(result.new_balance)}</span></div>}
                            </div>
                            <BioButton className="w-full mt-4" data-testid="pos-result-done" onClick={() => { if (result.ok) newSale(); setResult(null); }}>{result.ok ? t("pos.newSale") : t("common.back")}</BioButton>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
